import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, useMotionValue, useSpring } from 'framer-motion';
import { notification } from 'antd';
import PhoneInput from 'react-phone-number-input/input';
import isEmail from 'validator/lib/isEmail';
import {
  Zap, ShieldCheck, Headset, MessageSquare, Send,
  Phone, Users, Compass, Building2, Heart,
} from 'lucide-react';

import { getPageDataService, contactUsService } from '../../services/otherService';
import { checkStringContainsNumbers } from '../../helper';

const BRAND = '#842014';
const CREAM = '#fdf3f2';

// ─── AnimatedCounter ──────────────────────────────────────────────────────────
function AnimatedCounter({ value }) {
  const ref = useRef(null);
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, { damping: 60, stiffness: 100 });
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (isInView) motionValue.set(value);
  }, [isInView, motionValue, value]);

  useEffect(() => springValue.on('change', latest => setDisplay(Math.floor(latest))), [springValue]);

  return <span ref={ref}>{display.toLocaleString()}</span>;
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function Hero({ apiSection }) {
  const bg = apiSection?.image || 'https://cdn.magicpatterns.com/uploads/kaMmKHEiEQc6hySJBqA49Q/image_2026-04-15_170003359.png';
  const title = apiSection?.title || 'About Us';
  const desc = apiSection?.content || 'We are here to give families the best platform to find and register for activities, camps, and sports all in one place. Eliminate the search hassle. Eliminate the paperwork trouble.';

  return (
    <section style={{ position: 'relative', height: '60vh', minHeight: '440px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      <motion.div initial={{ scale: 1.05 }} animate={{ scale: 1 }} transition={{ duration: 1.5, ease: 'easeOut' }}
        style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `url("${bg}")`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.60)' }} />
      </motion.div>

      <div style={{ position: 'relative', zIndex: 10, maxWidth: '1152px', margin: '0 auto', padding: '0 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
          style={{ fontSize: 'clamp(2.25rem, 6vw, 3.75rem)', fontWeight: '800', color: '#fff', marginBottom: '16px', letterSpacing: '-0.02em', lineHeight: 1.1, fontFamily: "Georgia, 'Times New Roman', serif" }}>
          {title}
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
          style={{ fontSize: 'clamp(1rem, 1.5vw, 1.125rem)', color: '#e5e7eb', maxWidth: '768px', marginBottom: '32px', lineHeight: 1.75 }}>
          {desc}
        </motion.p>
        <motion.a href="/camps" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }}
          whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
          style={{ backgroundColor: BRAND, color: '#fff', padding: '12px 28px', borderRadius: '999px', fontWeight: '600', fontSize: '1rem', textDecoration: 'none', boxShadow: '0 8px 20px rgba(132,32,20,0.4)', display: 'inline-block', transition: 'background-color 0.2s' }}>
          Explore Activities
        </motion.a>
      </div>
    </section>
  );
}

// ─── MissionStats ─────────────────────────────────────────────────────────────
const STAT_ICONS = [Users, Compass, Building2, Heart];
const STAT_DEFAULTS = [
  { label: 'Participants', value: 10252 },
  { label: 'Unique Activities', value: 112 },
  { label: 'Amazing Organizations', value: 22 },
  { label: 'Caring Staff', value: 82 },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

function MissionStats({ missionSection, statSections }) {
  const missionText = missionSection?.content || '"To be the most convenient, secure, cost-effective solution for families to explore."';
  const missionTitle = missionSection?.title || 'Our Mission';

  const stats = statSections.length
    ? statSections.map((s, i) => ({ label: s.content, value: parseInt(s.title) || STAT_DEFAULTS[i]?.value || 0 }))
    : STAT_DEFAULTS;

  return (
    <section style={{ padding: '56px 24px', backgroundColor: CREAM, width: '100%' }}>
      <div style={{ maxWidth: '1152px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', alignItems: 'center' }} className="about-mission-grid">
        {/* Mission */}
        <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: '-100px' }} transition={{ duration: 0.8 }}
          style={{ maxWidth: '540px' }}>
          <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.25rem)', fontWeight: '700', color: '#1f2937', marginBottom: '16px', fontFamily: "Georgia, 'Times New Roman', serif" }}>{missionTitle}</h2>
          <div style={{ width: '80px', height: '4px', backgroundColor: BRAND, borderRadius: '999px', marginBottom: '20px' }} />
          <p style={{ fontSize: 'clamp(1.125rem, 2vw, 1.5rem)', color: '#374151', lineHeight: 1.7, fontStyle: 'italic', margin: 0 }}>
            {missionText}
          </p>
        </motion.div>

        {/* Stats grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {stats.slice(0, 4).map((stat, i) => {
            const Icon = STAT_ICONS[i] || Users;
            return (
              <motion.div key={i} variants={itemVariants}
                style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', transition: 'box-shadow 0.2s' }}>
                <div style={{ width: '56px', height: '56px', backgroundColor: CREAM, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', color: BRAND }}>
                  <Icon size={28} strokeWidth={2} />
                </div>
                <div style={{ fontSize: '2.25rem', fontWeight: '700', color: BRAND, marginBottom: '6px', fontFamily: "Georgia, 'Times New Roman', serif" }}>
                  <AnimatedCounter value={stat.value} />
                </div>
                <div style={{ fontSize: '0.75rem', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
      <style>{`@media(max-width:768px){.about-mission-grid{grid-template-columns:1fr !important;}}`}</style>
    </section>
  );
}

// ─── WhyChooseUs ──────────────────────────────────────────────────────────────
const WHY_DEFAULTS = [
  { title: 'Less Friction', description: "We make it as easy as possible for you to organize your schedule, find out what's new, and register!", icon: Zap },
  { title: 'More Secure', description: 'We keep all your personal information private and secure. We never sell your information or pass it on to any activity provider that you have not registered with.', icon: ShieldCheck },
  { title: 'Customer Service', description: 'Have a question or an issue? Let us know and we will make sure it gets resolved quickly and efficiently.', icon: Headset },
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

function WhyChooseUs({ apiSections }) {
  const cards = apiSections.length
    ? apiSections.map((s, i) => ({ title: s.title, description: s.content, icon: WHY_DEFAULTS[i]?.icon || Zap }))
    : WHY_DEFAULTS;

  return (
    <section style={{ padding: '56px 24px', backgroundColor: '#fff', width: '100%' }}>
      <div style={{ maxWidth: '1152px', margin: '0 auto' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-100px' }} transition={{ duration: 0.6 }}
          style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.25rem)', fontWeight: '700', color: '#1f2937', marginBottom: '16px', fontFamily: "Georgia, 'Times New Roman', serif" }}>Why Choose Us</h2>
          <div style={{ width: '96px', height: '4px', backgroundColor: BRAND, borderRadius: '999px', margin: '0 auto' }} />
        </motion.div>

        <motion.div variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.2 } } }}
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '32px' }}>
          {cards.map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.div key={i} variants={cardVariants} whileHover={{ y: -10 }}
                style={{ backgroundColor: '#fff', padding: '32px', borderRadius: '24px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', border: '1px solid #f9fafb', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', transition: 'all 0.3s' }}>
                <div style={{ width: '64px', height: '64px', backgroundColor: CREAM, borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px', color: BRAND, transform: 'rotate(3deg)' }}>
                  <Icon size={32} strokeWidth={2} />
                </div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '16px', fontFamily: "Georgia, 'Times New Roman', serif" }}>{card.title}</h3>
                <p style={{ color: '#4b5563', lineHeight: 1.7, margin: 0 }}>{card.description}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

// ─── HotlineBanner ────────────────────────────────────────────────────────────
function HotlineBanner() {
  return (
    <section style={{ width: '100%', backgroundColor: BRAND, padding: '20px 24px' }}>
      <div style={{ maxWidth: '1152px', margin: '0 auto' }}>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', color: '#fff', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ backgroundColor: 'rgba(255,255,255,0.2)', padding: '8px', borderRadius: '50%', display: 'flex' }}>
              <Phone size={24} color="#fff" />
            </div>
            <span style={{ fontSize: '1.25rem', fontWeight: '500', letterSpacing: '0.02em' }}>Hotline for Parents:</span>
          </div>
          <a href="tel:+19712453061"
            style={{ fontSize: 'clamp(1.5rem, 3vw, 1.875rem)', fontWeight: '700', color: '#fff', textDecoration: 'none', transition: 'opacity 0.2s', fontFamily: "Georgia, 'Times New Roman', serif" }}>
            +1 (971)-245-3061
          </a>
        </motion.div>
      </div>
    </section>
  );
}

// ─── ContactForm ──────────────────────────────────────────────────────────────
function ContactForm() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    else if (checkStringContainsNumbers(form.name)) e.name = 'Invalid name';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!isEmail(form.email)) e.email = 'Email is not valid';
    if (form.phone) {
      if (!/^\+[1-9]\d{7,14}$/.test(form.phone)) e.phone = 'Invalid phone number';
      else if (form.phone.replace(/\D/g, '').length !== 11) e.phone = 'Phone number must be exactly 10 digits';
    }
    if (!form.message.trim()) e.message = 'Message is required';
    return e;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    contactUsService({ name: form.name, phone: form.phone, email: form.email, message: form.message })
      .then(res => {
        setSubmitting(false);
        if (res.data.statusCode) {
          setForm({ name: '', email: '', phone: '', message: '' });
          notification.open({ description: res?.data?.responseData?.message || 'Message sent successfully!' });
        }
      }).catch(() => {
        setSubmitting(false);
        notification.open({ message: 'Error', description: 'Something went wrong. Please try again.' });
      });
  };

  const inputBase = {
    width: '100%', padding: '12px 16px', borderRadius: '12px',
    border: '1px solid #e5e7eb', outline: 'none', fontSize: '0.9375rem',
    backgroundColor: '#f9fafb', color: '#111827', boxSizing: 'border-box',
    transition: 'border-color 0.2s, background-color 0.2s', fontFamily: 'inherit',
  };
  const labelStyle = { fontSize: '0.875rem', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '6px' };
  const errStyle = { fontSize: '0.75rem', color: BRAND, marginTop: '4px', display: 'block' };
  const fieldWrap = { marginBottom: '20px' };

  return (
    <section style={{ padding: '56px 24px', backgroundColor: '#fff', width: '100%' }}>
      <div style={{ maxWidth: '1152px', margin: '0 auto' }}>
        <div style={{ borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid #f3f4f6', display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
          {/* Left dark panel */}
          <div style={{ flex: '0 0 40%', minWidth: '260px', backgroundColor: BRAND, padding: '40px', color: '#fff', display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: '256px', height: '256px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.08)', filter: 'blur(40px)', transform: 'translate(50%,-50%)' }} />
            <div style={{ position: 'absolute', bottom: 0, left: 0, width: '192px', height: '192px', borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.12)', filter: 'blur(30px)', transform: 'translate(-50%,50%)' }} />
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
              style={{ position: 'relative', zIndex: 1 }}>
              <MessageSquare size={40} style={{ marginBottom: '20px', opacity: 0.8 }} strokeWidth={1.5} />
              <h2 style={{ color: 'rgba(253,243,242,0.9)', fontSize: 'clamp(1.75rem, 3vw, 2.25rem)', fontWeight: '700', marginBottom: '16px', lineHeight: 1.2, fontFamily: "Georgia, 'Times New Roman', serif" }}>Get in Touch</h2>
              <p style={{ color: 'rgba(253,243,242,0.9)', lineHeight: 1.75, margin: 0, fontSize: '1rem' }}>
                We'd love to hear from you. Whether you have a question about activities, pricing, or anything else, our team is ready to answer all your questions.
              </p>
            </motion.div>
          </div>

          {/* Right form */}
          <div style={{ flex: 1, minWidth: '260px', padding: '40px', backgroundColor: '#fff' }}>
            <motion.form initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}
              onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }} className="about-contact-grid">
                <div>
                  <label style={labelStyle}>Full Name</label>
                  <input type="text" name="name" placeholder="Jane Doe" value={form.name} onChange={handleChange}
                    style={{ ...inputBase, ...(errors.name ? { borderColor: BRAND } : {}) }} required />
                  {errors.name && <span style={errStyle}>{errors.name}</span>}
                </div>
                <div>
                  <label style={labelStyle}>Email Address</label>
                  <input type="email" name="email" placeholder="jane@example.com" value={form.email} onChange={handleChange}
                    style={{ ...inputBase, ...(errors.email ? { borderColor: BRAND } : {}) }} required />
                  {errors.email && <span style={errStyle}>{errors.email}</span>}
                </div>
              </div>

              <div style={fieldWrap}>
                <label style={labelStyle}>Phone Number (Optional)</label>
                <PhoneInput country="US" placeholder="+1 (555) 000-0000" value={form.phone} maxLength={16}
                  onChange={val => { setForm(p => ({ ...p, phone: val || '' })); if (errors.phone) setErrors(p => ({ ...p, phone: '' })); }}
                  style={{ ...inputBase, ...(errors.phone ? { borderColor: BRAND } : {}) }} />
                {errors.phone && <span style={errStyle}>{errors.phone}</span>}
              </div>

              <div style={fieldWrap}>
                <label style={labelStyle}>Your Message</label>
                <textarea name="message" rows={4} placeholder="How can we help you today?" value={form.message} onChange={handleChange}
                  style={{ ...inputBase, resize: 'none', ...(errors.message ? { borderColor: BRAND } : {}) }} required />
                {errors.message && <span style={errStyle}>{errors.message}</span>}
              </div>

              <button type="submit" disabled={submitting}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '14px 32px', backgroundColor: BRAND, color: '#fff', border: 'none', borderRadius: '12px', fontWeight: '600', fontSize: '1rem', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1, boxShadow: '0 4px 12px rgba(132,32,20,0.3)', transition: 'background-color 0.2s' }}>
                {submitting ? 'Sending…' : 'Send Message'}
                {!submitting && <Send size={18} />}
              </button>
            </motion.form>
          </div>
        </div>
      </div>
      <style>{`@media(max-width:640px){.about-contact-grid{grid-template-columns:1fr !important;}}`}</style>
    </section>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
const AboutUsNew = () => {
  const [sections, setSections] = useState([]);

  useEffect(() => {
    getPageDataService('?page=about').then(res => {
      if (res?.data?.statusCode) setSections(res.data.responseData.result);
    }).catch(() => {});
  }, []);

  const get = type => sections.find(s => s.type === type);
  const getAll = types => sections.filter(s => types.includes(s.type));

  return (
    <div style={{ backgroundColor: '#fff' }}>
      <Hero apiSection={get('aboutSection1')} />
      <MissionStats
        missionSection={get('aboutSection2')}
        statSections={getAll(['aboutSection3','aboutSection4','aboutSection5','aboutSection6'])}
      />
      <WhyChooseUs apiSections={getAll(['aboutSection7','aboutSection8','aboutSection9'])} />
      <HotlineBanner />
      <ContactForm />
    </div>
  );
};

export default AboutUsNew;
